import { generatePropertyShortcutToNameMapFromDescriptors } from './utils';
import {
  CssProps,
  GridShortcuts,
  AlignItemsShortcuts,
  FlexDirectionShortcuts,
  JustifyContentShortcuts,
  PolymorphicProps,
  ResponsiveProperties,
  Margins,
  Paddings,
  FluidProperties,
  Gaps,
} from './types';
import { cloneElement, createElement, type ForwardedRef, forwardRef } from 'react';
import { ClassNames } from '@emotion/react';
import { type Descriptor, descriptors } from './descriptors';
import { theme, Breakpoint } from '@upright/ui/assets/styles/tokens';
import { mergeProps } from 'react-aria';

const descriptorValues = Object.values(descriptors);
const propertyShortcutToNameMap = generatePropertyShortcutToNameMapFromDescriptors(descriptorValues);
const breakpointKeysSet = new Set<unknown>(Object.keys(theme.breakpoint));
const sortedSpaceValues = toTypedEntries(theme.space).sort(([, a], [, b]) => parseFloat(b) - parseFloat(a));

export type Props = PolymorphicProps<
  ResponsiveProperties<
    Omit<
      CssProps,
      keyof (
        | GridShortcuts
        | AlignItemsShortcuts
        | FlexDirectionShortcuts
        | JustifyContentShortcuts
        | Margins
        | Paddings
        | Gaps
      )
    >
  > &
    (FluidProperties<Paddings> | ResponsiveProperties<Paddings>) &
    (FluidProperties<Margins> | ResponsiveProperties<Margins>) &
    (FluidProperties<Gaps> | ResponsiveProperties<Gaps>)
>;

function isBreakpointValue(value?: string): value is Breakpoint {
  return typeof value === 'string' && value in theme.breakpoint;
}

/** Build a @media rule for a given breakpoint */
function atBreakpoint(bp: string, styles: Record<string, string | undefined>) {
  const entries = Object.entries(styles).filter(([, v]) => v !== undefined);
  if (!entries.length) return {};
  if (!isBreakpointValue(bp)) return {};
  return {
    [`@media (min-width: ${theme.breakpoint[bp]})`]: Object.fromEntries(entries),
  };
}

function convertUnitlessNumberToPixelValue(value: string) {
  return /^-?[0-9]+$/.test(value) && value !== '0' ? `${value}px` : value;
}

export function camelCaseToKebabCase(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function toTypedEntries<K extends string, V>(obj?: Partial<Record<K, V>>): Array<[K, V]> {
  if (!obj) return [];
  return Object.entries(obj) as Array<[K, V]>;
}

function findDescriptorByPropertyName(propertyName: string): Descriptor | undefined {
  return descriptorValues.find(({ properties }) => {
    return properties.some(property => {
      const { name } = typeof property === 'string' ? { name: property } : property;
      const shortcut = typeof property === 'object' && 'shortcut' in property ? property.shortcut : undefined;
      return propertyName === name || propertyName === shortcut;
    });
  });
}

function getValuePropertyValueFromDescriptor({
  descriptor,
  value,
}: {
  descriptor: Descriptor;
  value: string | number;
}) {
  const { token, values, isUnitlessNumberAShortcutForPixelValue } = descriptor;
  const isExplicitlyDefinedValue = values?.map(String).includes(String(value));
  return token && !isExplicitlyDefinedValue
    ? `var(--${camelCaseToKebabCase(token)}__${value})`
    : isUnitlessNumberAShortcutForPixelValue
      ? convertUnitlessNumberToPixelValue(String(value))
      : String(value);
}

/** Build a CSS clamp() for fluid scaling between two breakpoints */
function fluidClamp(minValue: string, maxValue: string | number): string {
  const bpMin = `var(--breakpoint__md)`;
  const bpMax = `var(--breakpoint__2xl)`;
  // clamp(min, min + (max - min) * ((100vw - bp_min) / (bp_max - bp_min)), max)
  return [
    `clamp(`,
    `${minValue},`,
    ` calc(${minValue} + (${maxValue} - ${minValue}) * ((100vw - ${bpMin}) / (${bpMax} - ${bpMin}))),`,
    ` ${maxValue}`,
    `)`,
  ].join('');
}

function buildStylesAndFilterProps(props: Props) {
  type Key = keyof Props;
  const base: Partial<Record<string, string>> = {};
  const responsive: Record<string, Record<string, string>> = {};
  const fluid: Partial<Record<string, string | number>> = {};
  const restProps: Record<string, unknown> = {};

  for (const [key, value] of toTypedEntries(props)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (value === undefined) continue;
    if (typeof value !== 'string' && typeof value !== 'number') {
      restProps[key] = value;
      continue;
    }

    // Fluid prefix e.g. "fluid:padding"
    if (key.startsWith('fluid:')) {
      const prop = key.slice('fluid:'.length) as Key;
      fluid[prop] = value;
      continue;
    }

    // Responsive prefix e.g. "md:padding"
    const isResponsive = key.includes(':');

    if (isResponsive) {
      const [breakpoint, prop] = key.split(':');
      const descriptor = findDescriptorByPropertyName(prop);
      if (!descriptor) continue;
      if (!breakpointKeysSet.has(breakpoint)) continue;
      if (!(breakpoint in responsive)) responsive[breakpoint] = {};

      const nameFromShortcut = propertyShortcutToNameMap[prop];
      const name = typeof nameFromShortcut === 'string' ? nameFromShortcut : prop;
      responsive[breakpoint][name] = getValuePropertyValueFromDescriptor({
        descriptor,
        value,
      });
    } else {
      const descriptor = findDescriptorByPropertyName(key);
      const nameFromShortcut = propertyShortcutToNameMap[key];
      const name = typeof nameFromShortcut === 'string' ? nameFromShortcut : key;
      if (descriptor) {
        base[name] = getValuePropertyValueFromDescriptor({ descriptor, value });
      } else {
        restProps[key] = value;
      }
    }
  }

  // Merge fluid overrides: for each fluid property, look up the base value as the min
  // and the fluid value as the max, then replace the base declaration with a clamp().
  for (const [cssProperty, maxCssValue] of toTypedEntries(fluid)) {
    const descriptor = findDescriptorByPropertyName(cssProperty);
    if (!descriptor) continue;
    const maxCssValueIndex = sortedSpaceValues.findIndex(([key]) => key === maxCssValue);
    const minCssValue = sortedSpaceValues[maxCssValueIndex + 2][0];
    if (typeof maxCssValue === 'string') {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!minCssValue) {
        // No base value provided — fall back to the max value directly (no scaling)
        base[cssProperty] = getValuePropertyValueFromDescriptor({ descriptor, value: maxCssValue });
      } else {
        base[cssProperty] = fluidClamp(
          getValuePropertyValueFromDescriptor({ descriptor, value: minCssValue }),
          getValuePropertyValueFromDescriptor({ descriptor, value: maxCssValue }),
        );
      }
    }
  }

  // Merge responsive overrides
  const responsiveStyles: Record<string, string> = {};
  for (const [bp, bpStyles] of Object.entries(responsive)) {
    Object.assign(responsiveStyles, atBreakpoint(bp, bpStyles));
  }

  return {
    stylesObject: { ...base, ...responsiveStyles },
    restProps: restProps as unknown as PolymorphicProps<object>,
  };
}

export const Box = forwardRef(function _Box(props: Props, ref: ForwardedRef<HTMLDivElement>) {
  const { stylesObject, restProps } = buildStylesAndFilterProps(props);

  if (restProps.asChild) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, asChild, ...rest } = restProps;
    return (
      <ClassNames>
        {emotionProps => {
          const styles = emotionProps.css(stylesObject);
          return cloneElement(children, mergeProps(rest, children.props, { ref, className: styles }));
        }}
      </ClassNames>
    );
  }

  const { children, ...rest } = restProps;

  return (
    <ClassNames>
      {emotionProps => {
        const styles = emotionProps.css(stylesObject);
        return createElement('div', mergeProps(rest, { ref, className: styles }), children);
      }}
    </ClassNames>
  );
});
